import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

class JlStickySku extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.section = this.closest('.shopify-section');
    this.dialog = this.querySelector('dialog');
    this.options = this.querySelector('[data-sku-options]');
    this.status = this.querySelector('[data-sku-status]');
    this.sizeDialog = this.querySelector('.jl-sku-size-dialog');
    const { signal } = this.controller;
    if (this.picker()?.querySelector('[data-jl-color-links]')) this.linked = new LinkedSkuPurchase(this);
    if (!this.linked) this.nativePurchase = new NativeColourPurchase(this);
    this.section.addEventListener('click', event => {
      if ((!this.linked && !this.nativePurchase) || !event.target.closest('sticky-add-to-cart [ref="addToCartButton"]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.linked) this.linked.open();
      else this.renderOptions();
      if (!this.dialog.open) this.dialog.showModal();
    }, { capture: true, signal });
    this.section.addEventListener('click', event => {
      const button = event.target.closest('[data-open-sticky-sku]');
      if (!button) return;
      this.trigger = button;
      this.renderOptions();
      if (!this.dialog.open) this.dialog.showModal();
    }, { signal });
    this.addEventListener('click', event => {
      if (event.target.closest('[data-sku-size-guide]')) { this.openSizeGuide(); return; }
      if (event.target.closest('[data-close-sku-size]')) { this.sizeDialog.close(); return; }
      if (event.target.closest('[data-close-sku]')) this.dialog.close();
      if (this.linked) { this.linked.click(event); return; }
      const purchase = event.target.closest('[data-linked-purchase]');
      if (purchase && this.nativePurchase) { this.nativePurchase.purchase(purchase.dataset.linkedPurchase === 'checkout'); return; }
      const choice = event.target.closest('[data-sku-option]');
      if (!choice || this.pending) return;
      this.focusedOption = choice.dataset.skuOption;
      const nativeOption = [...(this.picker()?.querySelectorAll('[data-option-value-id]') || [])]
        .find(input => input.dataset.optionValueId === choice.dataset.skuOption);
      if (!nativeOption) return;
      if (nativeOption.tagName === 'OPTION') {
        nativeOption.parentElement.value = nativeOption.value;
        nativeOption.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (nativeOption.checked) {
        nativeOption.dispatchEvent(new Event('change', { bubbles: true }));
      } else nativeOption.click();
    }, { signal });
    this.dialog.addEventListener('click', event => {
      if (event.target !== this.dialog) return;
      const bounds = this.dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
    }, { signal });
    this.sizeDialog.addEventListener('click', event => {
      if (event.target !== this.sizeDialog) return;
      const r = this.sizeDialog.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.sizeDialog.close();
    }, { signal });
    this.sizeDialog.addEventListener('close', () => this.querySelector('[data-sku-size-guide]')?.focus({ preventScroll: true }), { signal });
    this.dialog.addEventListener('close', () => {
      this.section.querySelector('[data-open-sticky-sku]')?.focus({ preventScroll: true });
    }, { signal });
    this.section.addEventListener(StandardEvents.productSelect, event => {
      if (this.linked || !event.target.closest('.product-details') || !event.promise) return;
      const version = this.version = (this.version || 0) + 1;
      this.setPending(true);
      event.promise.then(({ detail }) => {
        if (version !== this.version) return;
        this.setPending(false);
        this.status.textContent = '';
        if (this.dialog.open) this.renderOptions();
        const addButton = this.section.querySelector('sticky-add-to-cart [ref="addToCartButton"]');
        if (addButton) addButton.disabled = !detail?.resource?.available;
      }).catch(error => {
        if (version !== this.version || error?.name === 'AbortError') return;
        this.setPending(false);
        this.status.textContent = this.dataset.error;
        const addButton = this.section.querySelector('sticky-add-to-cart [ref="addToCartButton"]');
        if (addButton) addButton.disabled = true;
        if (this.dialog.open) this.renderOptions();
      });
    }, { signal });
  }

  openSizeGuide() {
    const id = this.linked?.member?.dataset.jlLinkedProduct || this.nativePurchase?.product?.id;
    const template = [...this.querySelectorAll('template[data-sku-size-product]')].find(item => item.dataset.skuSizeProduct === String(id));
    if (!template) return;
    this.querySelector('[data-sku-size-content]').replaceChildren(template.content.cloneNode(true));
    if (!this.sizeDialog.open) this.sizeDialog.showModal();
  }

  picker() { return this.section.querySelector('.product-details variant-picker'); }

  setPending(value) {
    this.pending = value;
    this.options.setAttribute('aria-busy', String(value));

    if (this.nativePurchase) { this.nativePurchase.loading = value; this.nativePurchase.render(); }
    for (const button of this.options.querySelectorAll('button')) button.disabled = value;
    if (value) {
      this.status.textContent = this.dataset.loading;
      const addButton = this.section.querySelector('sticky-add-to-cart [ref="addToCartButton"]');
      if (addButton) addButton.disabled = true;
    }
  }

  renderOptions() {
    if (this.linked) { this.linked.open(); return; }
    const picker = this.picker();
    if (!picker) return;
    const previousFocus = this.options.querySelector(':focus')?.dataset.skuOption || this.focusedOption;
    const fragment = document.createDocumentFragment();
    const linkedColors = picker.querySelector('[data-jl-sibling-options]');
    if (linkedColors) {
      const group = document.createElement('fieldset');
      const legend = document.createElement('legend');
      legend.textContent = linkedColors.querySelector('[data-jl-color-heading]')?.textContent.trim() || '';
      group.append(legend, linkedColors.querySelector('[data-jl-color-links]').cloneNode(true));
      fragment.append(group);
    }
    for (const source of picker.querySelectorAll('fieldset, select')) {
      if (source.classList.contains('jl-native-linked-color')) continue;
      const inputs = source.tagName === 'SELECT' ? [...source.options] : [...source.querySelectorAll('input[type="radio"]')];
      if (!inputs.length) continue;
      const fieldset = document.createElement('fieldset');
      const legend = document.createElement('legend');
      legend.textContent = source.querySelector('legend')?.textContent.trim() || picker.querySelector(`label[for="${CSS.escape(source.id)}"]`)?.textContent.trim() || source.name;
      fieldset.append(legend);
      const row = document.createElement('div');
      row.className = 'jl-sku-choices';
      for (const input of inputs) {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.skuOption = input.dataset.optionValueId;
        button.textContent = input.value;
        button.setAttribute('aria-label', input.getAttribute?.('aria-label') || input.value);
        button.title = input.value;
        const palette = input.closest?.('label')?.querySelector('.jl-palette-fill');
        if (palette) {
          button.classList.add('jl-sku-choice--swatch');
          button.replaceChildren(palette.cloneNode(true));
        }
        button.setAttribute('aria-pressed', String(input.checked || input.selected || false));
        button.disabled = this.pending || this.nativePurchase?.submitting || input.disabled || input.dataset.optionAvailable === 'false';
        button.dataset.skuUnavailable = String(Boolean(input.disabled || input.dataset.optionAvailable === 'false'));
        if (input.dataset.optionAvailable === 'false') button.classList.add('jl-sku-unavailable');
        row.append(button);
      }
      fieldset.append(row);
      fragment.append(fieldset);
    }
    this.options.replaceChildren(fragment);
    if (previousFocus) [...this.options.querySelectorAll('button')].find(button => button.dataset.skuOption === previousFocus)?.focus({ preventScroll: true });
    const label = this.querySelector('[data-sku-current]');
    label.textContent = this.section.querySelector('[data-testid="sticky-variant-title"]')?.textContent.trim() || '';
    this.nativePurchase?.render();
  }

  disconnectedCallback() { this.version = (this.version || 0) + 1; this.controller?.abort(); this.linked?.disconnect(); }
}




class LinkedSkuPurchase {
  constructor(host) {
    this.host = host;
    this.members = [...host.picker().querySelectorAll('[data-jl-linked-product]')];
    this.member = this.members.find(link => link.getAttribute('aria-current') === 'page') || this.members[0];
    try { this.initialVariant = JSON.parse(host.picker().querySelector?.('script[type="application/json"]')?.textContent || 'null')?.id; } catch { this.initialVariant = null; }
    this.version = 0;
    this.submitting = false;

    this.details = host.querySelector('[data-sku-linked-details]');
    this.details.hidden = false;
  }

  open() {
    if (this.submitting) return;
    this.load(this.member);
  }

  async load(member) {
    if (this.submitting) return;
    const version = ++this.version;
    this.request?.abort();
    this.request = new AbortController();
    const preferred = this.product?.options.map((option, index) => [option.name, this.selected[index]]) || [];
    this.member = member;
    this.variant = null;
    this.loading = true;
    this.host.status.textContent = this.host.dataset.loading;
    this.render();
    try {
      const url = new URL(member.getAttribute('href'), window.location.origin);
      if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) throw new Error();
      url.pathname = url.pathname.replace(/\/$/, '') + '.js';
      url.search = '';
      const response = await fetch(url, { credentials: 'same-origin', signal: this.request.signal, cache: 'no-store' });
      if (!response.ok) throw new Error();
      const product = await response.json();
      if (version !== this.version) return;
      if (String(product.id) !== member.dataset.jlLinkedProduct || !Array.isArray(product.variants) || !Array.isArray(product.options)) throw new Error();
      this.product = product;
      const initial = !preferred.length && member.getAttribute('aria-current') === 'page' ? product.variants.find(variant => variant.available && variant.id === this.initialVariant) : null;
      const first = initial || product.variants.find(variant => variant.available && product.options.every((option, index) => {
        const saved = preferred.find(([name]) => name === option.name)?.[1];
        return !saved || !option.values.includes(saved) || variant.options[index] === saved;
      })) || product.variants.find(variant => variant.available) || product.variants[0];
      this.selected = first?.options.slice() || [];
      this.variant = first || null;
      this.loading = false;
      this.host.status.textContent = first?.available ? '' : this.host.dataset.unavailable;
      this.render();
    } catch (error) {
      if (version !== this.version || error.name === 'AbortError') return;
      this.loading = false;
      this.product = null;
      this.variant = null;
      this.host.status.textContent = this.host.dataset.error;
      this.render();
    }
  }

  click(event) {
    const color = event.target.closest('[data-linked-member]');
    if (color) {
      event.preventDefault();
      const member = this.members.find(link => link.dataset.jlLinkedProduct === color.dataset.linkedMember);
      if (member) this.load(member);
      return;
    }
    const option = event.target.closest('[data-linked-option]');
    if (option && !this.loading && !this.submitting && this.product) {
      const index = Number(option.dataset.linkedOption);
      const value = option.dataset.linkedValue;
      const candidates = this.product.variants.filter(variant => variant.available && variant.options[index] === value);
      const variant = candidates.find(variant => variant.options.every((v, i) => i === index || v === this.selected[i])) || candidates[0];
      if (variant) {
        this.selected = variant.options.slice();
        this.variant = variant;
        this.host.status.textContent = '';
        this.render();
      }
      return;
    }
    const action = event.target.closest('[data-linked-purchase]');
    if (action) this.purchase(action.dataset.linkedPurchase === 'checkout');
  }

  render() {
    const fragment = document.createDocumentFragment();
    const colors = document.createElement('fieldset');
    const legend = document.createElement('legend');
    legend.textContent = this.host.dataset.color + ': ' + (this.member.getAttribute('aria-label') || '').split(' — ')[0];
    const row = document.createElement('div');
    row.className = 'jl-sku-choices';
    for (const member of this.members) {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.linkedMember = member.dataset.jlLinkedProduct;
      button.setAttribute('aria-label', member.getAttribute('aria-label'));
      button.setAttribute('aria-pressed', String(member === this.member));
      button.title = member.title;
      const palette = member.querySelector('.jl-palette-fill');
      if (palette) {
        button.className = 'jl-sku-choice--swatch';
        button.append(palette.cloneNode(true));
      } else button.textContent = (member.getAttribute('aria-label') || '').split(' — ')[0];
      button.disabled = this.submitting;
      row.append(button);
    }
    colors.append(legend, row);
    fragment.append(colors);
    if (this.product && !this.loading) {
      this.product.options.forEach((option, index) => {
        if (option.values.length === 1 && /^(颜色|顏色|色块颜色|色塊顏色|colou?r|swatch colou?r)$/i.test(option.name)) return;
        const fieldset = document.createElement('fieldset');
        const title = document.createElement('legend');
        title.textContent = option.name;
        const choices = document.createElement('div');
        choices.className = 'jl-sku-choices';
        for (const value of option.values) {
          const button = document.createElement('button');
          button.type = 'button';
          button.textContent = value;
          button.dataset.linkedOption = String(index);
          button.dataset.linkedValue = value;
          button.setAttribute('aria-pressed', String(value === this.selected[index]));
          button.disabled = this.submitting || !this.product.variants.some(variant => variant.available && variant.options[index] === value);
          choices.append(button);
        }
        fieldset.append(title, choices);
        fragment.append(fieldset);
      });
    }
    this.host.options.replaceChildren(fragment);
    this.host.options.setAttribute('aria-busy', String(this.loading));
    this.host.querySelector('[data-sku-current]').textContent = this.product && !this.loading ? this.product.title : this.member.title;
    const money = this.details.querySelector('[data-linked-price]');
    money.textContent = this.variant ? new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.host.dataset.currency }).format(this.variant.price / 100) : '';
    const image = this.details.querySelector('img');
    const imageSource = !this.loading && this.product ? this.variant?.featured_image?.src || this.product.featured_image : null;
    image.hidden = !imageSource;
    if (imageSource) { image.src = imageSource; image.alt = this.product.title; }
    for (const button of this.details.querySelectorAll('[data-linked-purchase]')) button.disabled = this.loading || this.submitting || !this.variant?.available;
    this.details.querySelector('input').disabled = this.submitting;
  }

  async purchase(checkout) {
    if (this.loading || this.submitting || !this.variant?.available) return;
    const input = this.details.querySelector('input');
    if (!input.reportValidity()) return;
    const quantity = Number(input.value);
    if (!Number.isSafeInteger(quantity) || quantity < 1) return;
    const variantId = this.variant.id;
    this.submitting = true;
    this.host.status.textContent = this.host.dataset.loading;
    this.render();
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    this.host.dialog.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: String(variantId), quantity }], promise: deferred.promise }));
    let added = false;
    try {
      const root = window.Shopify.routes.root;
      const sections = [...document.querySelectorAll('cart-items-component')].map(element => element.dataset.sectionId).filter(Boolean).slice(0, 5);
      const response = await fetch(root + 'cart/add.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: variantId, quantity }], ...(sections.length ? { sections: sections.join(','), sections_url: window.location.pathname } : {}) }) });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || this.host.dataset.error);
      added = true;
      this.host.status.textContent = this.host.dataset.added;
      if (checkout) window.location.assign(root + 'checkout');
      const cartResponse = await fetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store' });
      if (!cartResponse.ok) throw new Error();
      const cart = await cartResponse.json();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'jl-linked-sku', sourceId: this.host.id, productId: String(this.product.id), itemCount: quantity, sections: result.sections, didError: false } });
    } catch (error) {
      this.host.status.textContent = added ? this.host.dataset.added : error.message || this.host.dataset.error;
      try {
        const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js', { credentials: 'same-origin', cache: 'no-store' });
        if (!cartResponse.ok) throw new Error();
        const cart = await cartResponse.json();
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'jl-linked-sku', sourceId: this.host.id, productId: String(this.product.id), itemCount: cart.item_count, didError: true } });
      } catch {
        deferred.reject(error);
      }
    } finally {
      this.submitting = false;
      if (this.host.isConnected) this.render();
    }
  }

  disconnect() { this.version++; this.request?.abort(); }
}



class NativeColourPurchase {
  constructor(host) {
    this.host = host;
    this.product = JSON.parse((host.section.querySelector('[data-jl-colour-product]') || host.querySelector('[data-jl-sku-product]')).textContent);
    this.details = host.querySelector('[data-sku-linked-details]');
    this.details.hidden = false;

    this.loading = false;
    this.submitting = false;
  }
  render() {
    try { this.variant = JSON.parse(this.host.picker()?.querySelector('script[type="application/json"]')?.textContent || 'null'); } catch { this.variant = null; }
    const variant = this.variant;
    const image = this.details.querySelector('img');
    const selected = this.product.variants.find(item => String(item.id) === String(variant?.id));
    const src = selected?.featured_image?.src || this.product.featured_image;
    image.hidden = !src;
    if (src) { image.src = src; image.alt = this.product.title; }
    this.details.querySelector('[data-linked-price]').textContent = variant ? new Intl.NumberFormat(document.documentElement.lang || 'en', { style:'currency', currency:this.host.dataset.currency }).format(variant.price / 100) : '';
    for (const button of this.details.querySelectorAll('[data-linked-purchase]')) button.disabled = this.loading || this.submitting || !variant?.available;
    for (const button of this.host.options.querySelectorAll('button')) button.disabled = this.loading || this.submitting || button.dataset.skuUnavailable === 'true';
    this.details.querySelector('input').disabled = this.submitting;
  }
  purchase(checkout) { return LinkedSkuPurchase.prototype.purchase.call(this, checkout); }
}

if (!customElements.get('jl-sticky-sku')) customElements.define('jl-sticky-sku', JlStickySku);

